import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import catalogStyles from 'AppRoutes/BeerStore/styles';
import heroGrowlersMobile from 'assets/icons/hero_growlers_mobile2.webp';
import SEOHead from 'components/SEOHead';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { getGrowlersHero } from 'services/firebase/growlerHeroService';
import { GrowlerService } from 'services/firebase/growlerService';
import {
  getGrowlerPackageType,
  GROWLERS_CATALOG_HASH,
  scrollToGrowlersCatalog,
} from 'utils/growlersFlow';
import type { Growler, GrowlersHero } from 'utils/types/Growler';

import GrowlerCatalogCard from './GrowlerCatalogCard';
import styles from './styles';

const Growlers: React.FC = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { addToCart, openCart } = useGrowlerCart();
  const [growlers, setGrowlers] = useState<Growler[]>([]);
  const [hero, setHero] = useState<GrowlersHero | null>(null);
  const [stylesList, setStylesList] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [items, filterOptions, heroData] = await Promise.all([
          GrowlerService.getAllGrowlers(),
          GrowlerService.getFilterOptions(),
          getGrowlersHero(),
        ]);
        setGrowlers(items);
        setStylesList(filterOptions.styles);
        setHero(heroData);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar o catálogo. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (hash !== `#${GROWLERS_CATALOG_HASH}`) {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollToGrowlersCatalog();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash, loading]);

  const filteredGrowlers = useMemo(
    () =>
      growlers.filter(item => {
        const matchesSearch =
          !searchTerm ||
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.brewery.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.style.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStyle =
          selectedStyles.length === 0 || selectedStyles.includes(item.style);
        return matchesSearch && matchesStyle;
      }),
    [growlers, searchTerm, selectedStyles],
  );

  const growlerItems = useMemo(
    () =>
      filteredGrowlers.filter(
        item => getGrowlerPackageType(item) === 'growler',
      ),
    [filteredGrowlers],
  );

  const bottleItems = useMemo(
    () =>
      filteredGrowlers.filter(item => getGrowlerPackageType(item) === 'bottle'),
    [filteredGrowlers],
  );

  const handleAddToCart = (growler: Growler) => {
    addToCart(growler);
    openCart();
  };

  const renderGrid = (items: Growler[]) => (
    <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
      {items.map(item => (
        <Grid item xs={6} sm={6} md={4} lg={3} key={item.id}>
          <GrowlerCatalogCard
            growler={item}
            onOpen={id => navigate(`/growlers/${id}`)}
            onAdd={handleAddToCart}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={styles.page}>
      <SEOHead
        title="Growlers e Garrafas | Chope na porta da sua casa - Clube da 42"
        description="Peça growlers de chope e garrafas artesanais e receba na porta de casa. Estilos selecionados da Cervejaria 42."
        keywords="growler, garrafa, chope artesanal, clube da 42, chope em casa"
      />

      <Box sx={styles.hero}>
        <Box
          component="img"
          src={heroGrowlersMobile}
          alt={hero?.alt || 'Chope na porta da sua casa'}
          sx={styles.heroImageMobile}
        />
        {hero?.image && (
          <Box
            component="img"
            src={hero.image}
            alt={hero.alt || 'Chope na porta da sua casa'}
            sx={styles.heroImageDesktop}
          />
        )}
        <Box sx={styles.heroOverlay} />
        <Box sx={styles.heroContent}>
          <Typography sx={styles.heroEyebrow}>Clube da 42</Typography>
          <Typography component="h1" sx={styles.heroTitle}>
            Cerveja e chope na porta da sua casa
          </Typography>
          <Typography sx={styles.heroSubtitle}>
            Growlers e garrafas, gelados e prontos para servir. Escolha o
            estilo, peça online e receba em sua casa.
          </Typography>
        </Box>
      </Box>

      <Container sx={styles.catalogSection}>
        <Typography
          id={GROWLERS_CATALOG_HASH}
          variant="h4"
          sx={styles.catalogTitle}
        >
          Growlers e garrafas
        </Typography>
        <Typography sx={styles.catalogSubtitle}>
          Escolha o formato, o estilo e finalize o pedido em poucos passos.
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <TextField
              fullWidth
              placeholder="Buscar produto, estilo ou cervejaria..."
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              sx={{ mb: 2, ...catalogStyles.searchField }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {stylesList.length > 0 && (
              <Box sx={catalogStyles.stylePills}>
                <Chip
                  label="Todos"
                  size="small"
                  clickable
                  onClick={() => setSelectedStyles([])}
                  sx={
                    selectedStyles.length === 0
                      ? catalogStyles.stylePillActive
                      : catalogStyles.stylePill
                  }
                />
                {stylesList.map(style => {
                  const isSelected = selectedStyles.includes(style);
                  return (
                    <Chip
                      key={style}
                      label={style}
                      size="small"
                      clickable
                      onClick={() =>
                        setSelectedStyles(prev =>
                          isSelected
                            ? prev.filter(item => item !== style)
                            : [...prev, style],
                        )
                      }
                      sx={
                        isSelected
                          ? catalogStyles.stylePillActive
                          : catalogStyles.stylePill
                      }
                    />
                  );
                })}
              </Box>
            )}

            <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
              {filteredGrowlers.length} produto
              {filteredGrowlers.length !== 1 ? 's' : ''} encontrado
              {filteredGrowlers.length !== 1 ? 's' : ''}
            </Typography>

            {filteredGrowlers.length === 0 && (
              <Alert severity="info">Nenhum produto encontrado.</Alert>
            )}

            {growlerItems.length > 0 && (
              <Box id="growlers-section" sx={styles.subsection}>
                <Typography component="h2" sx={styles.subsectionTitle}>
                  Growlers
                </Typography>
                <Typography sx={styles.subsectionSubtitle}>
                  Chope artesanal para levar e servir em casa.
                </Typography>
                {renderGrid(growlerItems)}
              </Box>
            )}

            {bottleItems.length > 0 && (
              <Box id="garrafas" sx={styles.subsection}>
                <Typography component="h2" sx={styles.subsectionTitle}>
                  Garrafas
                </Typography>
                <Typography sx={styles.subsectionSubtitle}>
                  Cervejas artesanais em garrafa, no mesmo checkout.
                </Typography>
                {renderGrid(bottleItems)}
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default Growlers;
